import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

// Stock reset: declaring a physical count to be the truth for a van or a
// storeroom. Every call is gated to Inventory & Operations by the tech server.
const BASE = '/api/stock-reset';

export function useResetTargets() {
  return useQuery({
    queryKey: ['stockReset', 'targets'],
    queryFn: async () => (await api.get(`${BASE}/targets`)).data,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

// Every count this location has, newest first, with the one currently acting as
// its opening balance marked.
export function useResetCounts(type, target) {
  return useQuery({
    queryKey: ['stockReset', 'counts', type || '', String(target || '')],
    queryFn: async () => (await api.get(
      `${BASE}/counts?type=${type}&target=${encodeURIComponent(target)}`)).data,
    enabled: Boolean(type && target),
    staleTime: 0,
    retry: false,
  });
}

// What the reset would do, before anybody commits to it.
export function useResetPreview(type, target, countId) {
  return useQuery({
    queryKey: ['stockReset', 'preview', type || '', String(target || ''), String(countId || '')],
    queryFn: async () => (await api.get(
      `${BASE}/preview?type=${type}&target=${encodeURIComponent(target)}&count_id=${countId}`)).data,
    enabled: Boolean(type && target && countId),
    staleTime: 0,
    retry: false,
  });
}

export function useApplyReset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post(`${BASE}/apply`, body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stockReset'] });
      // The levels screen reads the same figures — a reset that left it showing
      // yesterday's numbers would have somebody resetting twice.
      qc.invalidateQueries({ queryKey: ['stockLevels'] });
    },
  });
}

export function useResetHistory() {
  return useQuery({
    queryKey: ['stockReset', 'history'],
    queryFn: async () => (await api.get(`${BASE}/history`)).data.resets,
    staleTime: 0,
    retry: false,
  });
}
